import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import {
  DateFieldComponent,
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
} from '@custom-components';
import { InsightsService } from '@core/services/insights.service';
import { gunzipCsvUtil, parseContentDispositionFilename } from '@core/utils/gunzip-csv.util';

export interface DeviceCsvExportDialogData {
  deviceId: string;
  ldn: string;
}

@Component({
  selector: 'xemex-device-csv-export-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DateFieldComponent,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'nl-NL' }],
  templateUrl: './device-csv-export-dialog.component.html',
  styleUrl: './device-csv-export-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceCsvExportDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<DeviceCsvExportDialogComponent>);
  private readonly data = inject<DeviceCsvExportDialogData>(MAT_DIALOG_DATA);
  private readonly insightsService = inject(InsightsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isDownloading = signal(false);
  readonly hasError = signal(false);
  readonly maxDate = new Date();

  readonly exportForm = new FormGroup({
    startDate: new FormControl<Date | null>(this.defaultStart(), Validators.required),
    endDate: new FormControl<Date | null>(new Date(), Validators.required),
  });

  private defaultStart(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  protected isRangeInvalid(): boolean {
    const { startDate, endDate } = this.exportForm.value;
    return !!startDate && !!endDate && startDate > endDate;
  }

  protected canDownload(): boolean {
    return this.exportForm.valid && !this.isRangeInvalid() && !this.isDownloading();
  }

  onDownload(): void {
    if (!this.canDownload()) {
      this.exportForm.markAllAsTouched();
      return;
    }

    const start = this.toIsoDate(this.exportForm.value.startDate!);
    const end = this.toIsoDate(this.exportForm.value.endDate!);

    this.isDownloading.set(true);
    this.hasError.set(false);

    this.insightsService
      .exportDeviceCsv(this.data.deviceId, this.data.ldn, start, end)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async response => {
          try {
            if (!response.body) {
              this.hasError.set(true);
              return;
            }
            await this.triggerDownload(response, start, end);
            this.dialogRef.close(true);
          } catch {
            this.hasError.set(true);
          } finally {
            this.isDownloading.set(false);
          }
        },
        error: () => {
          this.isDownloading.set(false);
          this.hasError.set(true);
        },
      });
  }

  private async triggerDownload(
    response: HttpResponse<Blob>,
    start: string,
    end: string,
  ): Promise<void> {
    const blob = response.body;
    if (!blob) return;

    // Server returns a gzipped CSV; decompress so the user gets a plain .csv file.
    const rawName =
      parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
      `${this.data.ldn}_${start}_${end}.csv.gz`;
    const filename = rawName.replace(/\.gz$/i, '');
    const csvBlob = await gunzipCsvUtil.gunzipToCsvBlob(blob);

    gunzipCsvUtil.triggerBlobDownload(csvBlob, filename);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
